import type { ReactNode } from 'react';
import {
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  Replay as ReplayIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import { Alert, Box, Card, CardContent, IconButton, Skeleton, Typography } from '@mui/material';
import { formatDistanceToNow, isValid } from 'date-fns';
import { useInfoBoardStats } from './useInfoBoardStats';
import type { InfoBoardProps } from './types';
import { formatDuration } from './utils';

const formatLastWorked = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;

  const date = new Date(value);
  if (!isValid(date)) return undefined;

  return `Last worked ${formatDistanceToNow(date, { addSuffix: true })}`;
};

type InfoCardProps = {
  title: string;
  value: string;
  subtitle?: string;
  icon: ReactNode;
  loading: boolean;
};

const InfoCard = ({ title, value, subtitle, icon, loading }: InfoCardProps) => (
  <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
    <CardContent sx={{ display: 'flex', minHeight: 120, flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {loading ? (
          <Skeleton variant="text" width={80} height={20} />
        ) : (
          <Typography variant="subtitle2">{title}</Typography>
        )}
        <IconButton
          size="small"
          disabled
          aria-hidden="true"
          sx={{
            bgcolor: 'action.hover',
            color: 'text.secondary',
            '&.Mui-disabled': {
              bgcolor: 'action.hover',
              color: 'text.secondary',
            },
          }}
        >
          {icon}
        </IconButton>
      </Box>

      {loading ? (
        <Skeleton variant="text" width={110} height={32} sx={{ mt: 1 }} />
      ) : (
        <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
          {value}
        </Typography>
      )}

      {loading ? (
        <Skeleton variant="text" width={130} height={16} />
      ) : subtitle ? (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

export const InfoBoard = ({ relationId }: InfoBoardProps) => {
  const { data, loading, error } = useInfoBoardStats(relationId);
  const plannedHours = data?.ThisWeek?.PlannedHours;
  const plannedHoursSubtitle =
    typeof plannedHours === 'number' && Number.isFinite(plannedHours)
      ? `of ${plannedHours} hrs weekly limit`
      : undefined;

  const cards: Array<Omit<InfoCardProps, 'loading'>> = [
    {
      title: 'Last 24 hours',
      value: formatDuration(data?.Last24Hours?.Total),
      subtitle: formatLastWorked(data?.Last24Hours?.LastWorkLogWasTrackedUTC),
      icon: <AccessTimeIcon fontSize="small" />,
    },
    {
      title: 'This week',
      value: formatDuration(data?.ThisWeek?.Total),
      subtitle: plannedHoursSubtitle,
      icon: <CalendarTodayIcon fontSize="small" />,
    },
    {
      title: 'Last week',
      value: formatDuration(data?.LastWeek?.Total),
      icon: <ReplayIcon fontSize="small" />,
    },
    {
      title: 'Since start',
      value: formatDuration(data?.SinceStart?.Total),
      icon: <ShowChartIcon fontSize="small" />,
    },
  ];

  return (
    <Box sx={{ width: '100%', mt: 2, mb: 1 }}>
      {error && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {cards.map((card) => (
          <InfoCard key={card.title} {...card} loading={loading} />
        ))}
      </Box>
    </Box>
  );
};

export default InfoBoard;
